import type { StoreSettings } from '@avrahami/contracts';
import { Alert, Loading } from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { keys, settingsQuery, tenantApi } from '../api/tenant';
import { errorMessage, NO_ERRORS, type FieldErrors } from '../lib/forms';
import { settingsDraft, settingsRequest } from '../lib/settings';
import { mergeErrors } from './fields';
import { useVersionedDraft } from './versioned';

/** Renders its children with the store's settings once they are loaded. */
export function WithSettings({ children }: { children: (settings: StoreSettings) => ReactNode }) {
  const settings = useQuery(settingsQuery());
  if (settings.isPending) return <Loading />;
  if (settings.isError) return <Alert>{errorMessage(settings.error)}</Alert>;
  return children(settings.data);
}

/**
 * Editing the settings resource (settings page, analytics page): a draft of the whole
 * object, saved whole with the version the draft was made from; a change made elsewhere
 * meanwhile answers CONFLICT, and "reload" starts again from the server's copy.
 */
export function useSettingsEditor(settings: StoreSettings) {
  const queryClient = useQueryClient();
  const editor = useVersionedDraft(settings, (value) => value.version, settingsDraft);
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const save = useMutation({
    mutationFn: tenantApi.updateSettings,
    onSuccess: async (updated) => {
      editor.reset(updated);
      queryClient.setQueryData(keys.settings(), updated);
      await queryClient.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = settingsRequest(editor.draft, editor.baseVersion);
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(parsed.value);
  };
  const reload = () => {
    save.reset();
    setLocalErrors(NO_ERRORS);
    void queryClient
      .query({ ...settingsQuery(), staleTime: 0 })
      // A failed read shows on the page itself (the settings query's error).
      .then(editor.reset, () => undefined);
  };
  return {
    draft: editor.draft,
    set: editor.set,
    stale: editor.stale,
    reload,
    submit,
    errors: mergeErrors(localErrors, save.error),
    localInvalid: Object.keys(localErrors).length > 0,
    save,
  };
}
